import clsx from "clsx";
import {
  ComponentPropsWithoutRef,
  FC,
  forwardRef,
  ReactNode,
  useState,
} from "react";
import "./scroll-bar.css";
import { useScrollbarWidth } from "@/hooks/use-scrollbar-width";

interface TableComponent
  extends FC<
    ComponentPropsWithoutRef<"div"> & {
      className?: string;
    }
  > {
  Header: FC<ComponentPropsWithoutRef<"div">>;
  HeaderColumn: FC<
    ComponentPropsWithoutRef<"div"> & {
      className?: string;
    }
  >;
  Body: FC<ComponentPropsWithoutRef<"div">>;
  Row: FC<
    ComponentPropsWithoutRef<"div"> & {
      expandedSection?: ReactNode;
      className?: string;
    }
  >;
  Column: FC<ComponentPropsWithoutRef<"div">>;
}

const Table: TableComponent = ({ children, className, ...rest }) => {
  return (
    <div className={clsx("flex-1 flex", className)} {...rest}>
      <div className="w-full flex-1 flex flex-col">{children}</div>
    </div>
  );
};

const Header: TableComponent["Header"] = ({
  children,
  className = "",
  ...rest
}) => {
  const scrollBarWidth = useScrollbarWidth();

  return (
    <div
      className={clsx(
        "dark:bg-black dark:text-white flex flex-row sticky top-0",
        className
      )}
      style={{ borderRight: `${scrollBarWidth}px solid black` }}
      {...rest}
    >
      {children}
    </div>
  );
};

const HeaderColumn: TableComponent["HeaderColumn"] = ({
  children,
  className,
  ...rest
}) => {
  return (
    <div
      className={clsx(
        "font-semibold text-xs",
        className,
        !className && "min-w-[4.7%] text-left"
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

const Body: TableComponent["Body"] = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div">
>(({ children, id = "", ...rest }, ref) => {
  return (
    <div
      className="flex-1 flex flex-col overflow-y-scroll arkaine-scrollbar"
      ref={ref}
      id={id}
      {...rest}
    >
      {children}
    </div>
  );
});

const Row: TableComponent["Row"] = ({
  children,
  expandedSection = null,
  className = "text-left",
  ...rest
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <>
      <div
        className={clsx(
          "cursor-pointer border-b border-[#121521] flex flex-row relative gradient-box before:py-[1px] before:hover:bg-gradient-100 hover:bg-table-hover",
          isExpanded && "bg-gradient-to-r",
          className
        )}
        onClick={() => setIsExpanded((prev: boolean) => !prev)}
        {...rest}
      >
        {children}
      </div>

      {isExpanded && expandedSection}
    </>
  );
};

const Column: TableComponent["Column"] = ({
  children,
  className = "",
  ...rest
}) => {
  return (
    <div className={clsx(!className && "min-w-[4.7%]", className)} {...rest}>
      {children}
    </div>
  );
};

Table.Row = Row;
Table.Column = Column;
Table.Header = Header;
Table.HeaderColumn = HeaderColumn;
Table.Body = Body;

export default Table;
